import { useMemo } from 'react';
import { uniqBy } from 'lodash-es';
import cn from 'classnames';
import { useDayColumns, useSchedulerTableAutoScroll } from '@lp/features/Scheduler/hooks';
import { getColumnHeaderClassname } from '@lp/features/Scheduler/utils';
import { stickyShadowStyles } from '@lp/features/Scheduler/constants';
import { isSameCSWindow } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import { CSOnCallTotalsSubTable } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/components/CSSchedulerTable/components';
import { Region, CSResource, CSResourcesWithShifts } from '@/api/apis/resources/schedule';

type SchedulerRegionTotalsTableProps = {
  region: Region;
  selectedDate: Date;
  daysInterval: Date[];
  shiftsData: CSResourcesWithShifts;
  hubToResourceMap: Map<string, CSResource[]> | null;
};

export const SchedulerRegionTotalsTable = ({
  region,
  selectedDate,
  daysInterval,
  shiftsData,
  hubToResourceMap,
}: SchedulerRegionTotalsTableProps) => {
  const { scrollContainerRef, columnRefCallback } = useSchedulerTableAutoScroll(selectedDate);
  const dayCols = useDayColumns(daysInterval, selectedDate, isSameCSWindow);

  const regionHubIds = useMemo(() => region.hubs.map((h) => h.id), [region]);
  const userList = useMemo(() => {
    return uniqBy(
      regionHubIds.flatMap((hubId) => hubToResourceMap?.get(hubId) ?? []),
      'id',
    );
  }, [regionHubIds, hubToResourceMap]);

  return (
    <div ref={scrollContainerRef} className="w-full overflow-auto bg-white rounded-lg">
      <table aria-label="Region totals table" className="relative border-separate border-spacing-0 pb-2 pr-4">
        <thead>
          <tr className="h-10 text-left [&>th]:px-3 [&>th]:text-s-sb first:[&>th]:rounded-st-lg last:[&>th]:rounded-et-lg">
            <th
              className={cn('bg-base-80 text-base-20 sticky min-w-[220px] left-0 z-11 uppercase', stickyShadowStyles)}
            >
              {region.name} totals
            </th>
            {dayCols.map((column) => (
              <th
                ref={(el) => columnRefCallback(el, column.date)}
                key={column.key}
                className={cn('min-w-[158px]', getColumnHeaderClassname(column, 'bg-base-80'))}
              >
                <span className={cn({ 'opacity-60': !column.isCurrentMonth })}>{column.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <CSOnCallTotalsSubTable hubIds={regionHubIds} dayCols={dayCols} userList={userList} shiftsData={shiftsData} />
        </tbody>
      </table>
    </div>
  );
};
